import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DomainIcon from '@mui/icons-material/Domain';
import HandymanIcon from '@mui/icons-material/Handyman';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentsIcon from '@mui/icons-material/Payments';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ConstructionIcon from '@mui/icons-material/Construction';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { SvgIconProps } from '@mui/material';
import { Permissions } from '@billda/permissions-ui';
import { Features } from '@billda/features-ui';
import { Routes as AppRoutes } from '../../../routes';

export interface NavigationItem {
  text: string;
  icon: React.ComponentType<SvgIconProps>;
  path: string;
  subItems?: NavigationItem[];
  permission?: string; // optional permission to gate visibility
  featureFlag?: string; // optional feature flag to gate visibility
  tooltip?: string; // tooltip text for icon-only display
  action?: 'logout'; // optional action instead of navigation
  highlight?: boolean; // draws attention with accent styling in sidebar
}

// Top-level items rendered above group headers (after Dashboard/Search)
export const topNavItems: NavigationItem[] = [
  {
    text: 'Dashboard',
    icon: DashboardIcon,
    path: AppRoutes.dashboard,
    featureFlag: Features.Dashboard.IsEnabled,
    tooltip: 'Dashboard',
  },
  {
    text: 'Getting Started',
    icon: RocketLaunchIcon,
    path: AppRoutes.onboarding.gettingStarted,
    permission: Permissions.Organisation.Manage,
    tooltip: 'Getting Started',
    highlight: true,
  },
];

// Main navigation items (top section of sidebar)
export const primaryNavItems: NavigationItem[] = [
  {
    text: 'Projects',
    icon: ConstructionIcon,
    path: AppRoutes.sites.manage,
    tooltip: 'Projects',
    subItems: [
      {
        text: 'Activity',
        icon: HistoryIcon,
        path: AppRoutes.activity.manage,
        // Admin-only cross-estate view. The route and API enforce both
        // Sites.All and Labour.All; the nav gates on Sites.All as the hint.
        permission: Permissions.Sites.All,
        featureFlag: Features.WorkActivity.IsEnabled,
      },
      {
        text: 'Sites',
        icon: DomainIcon,
        path: AppRoutes.sites.manage,
        permission: Permissions.Sites.Read,
      },
      {
        text: 'Labour',
        icon: HandymanIcon,
        path: AppRoutes.labour.manage,
        permission: Permissions.Labour.Read,
      },
      {
        text: 'Variations',
        icon: ChangeCircleOutlinedIcon,
        path: AppRoutes.variations.manage,
        permission: Permissions.Variations.Read,
      },
      {
        text: 'Files',
        icon: FolderOutlinedIcon,
        path: AppRoutes.files.root,
        permission: Permissions.Files.Read,
        featureFlag: Features.Files.IsEnhancedFileManagerEnabled,
      },
    ],
  },
  {
    text: 'Workforce',
    icon: PersonIcon,
    path: AppRoutes.users.manage,
    tooltip: 'Workforce',
    subItems: [
      {
        text: 'Users',
        icon: PersonIcon,
        path: AppRoutes.users.manage,
        permission: Permissions.Users.Manage,
      },
      {
        text: 'Gangs',
        icon: GroupWorkIcon,
        path: AppRoutes.gangs.manage,
        permission: Permissions.Gangs.Read,
      },
    ],
  },
  {
    text: 'Finance',
    icon: AccountBalanceIcon,
    path: AppRoutes.finance.pots,
    permission: Permissions.Pots.All.Read,
    tooltip: 'Finance',
    subItems: [
      {
        text: 'Pots',
        icon: AccountBalanceWalletIcon,
        path: AppRoutes.finance.pots,
        permission: Permissions.Pots.All.Read,
      },
      {
        text: 'Payments',
        icon: PaymentsIcon,
        path: AppRoutes.finance.payments,
        permission: Permissions.Pots.Pay,
      },
      {
        text: 'Deductions',
        icon: RemoveCircleOutlineIcon,
        path: AppRoutes.finance.deductions,
        permission: Permissions.Deductions.All.Read,
      },
    ],
  },
];

// Platform admin items (internal management)
export const platformNavItems: NavigationItem[] = [
  {
    text: 'Platform',
    icon: AdminPanelSettingsIcon,
    path: AppRoutes.platform.organisations,
    permission: Permissions.Platform.Organisations.Manage,
    tooltip: 'Platform',
    subItems: [
      {
        text: 'Organisations',
        icon: BusinessIcon,
        path: AppRoutes.platform.organisations,
        permission: Permissions.Platform.Organisations.Manage,
      },
      {
        text: 'Feature Switches',
        icon: ToggleOnIcon,
        path: AppRoutes.platform.features,
        permission: Permissions.Platform.Features.Read,
      },
    ],
  },
];

// Utility navigation items (bottom section of sidebar)
export const secondaryNavItems: NavigationItem[] = [
  {
    text: 'Support',
    icon: SupportAgentIcon,
    path: '/support',
    tooltip: 'Support',
  },
  {
    text: 'Settings',
    icon: SettingsIcon,
    path: AppRoutes.settings,
    tooltip: 'Settings',
  },
];

/** All nav items flattened (hoisted to avoid hot-path array spread) */
export const ALL_NAV_ITEMS: NavigationItem[] = [
  ...topNavItems,
  ...primaryNavItems,
  ...platformNavItems,
  ...secondaryNavItems,
];

/** Find the current page's nav item (including sub-items) based on pathname. */
export function findActiveNavItem(
  pathname: string
): NavigationItem | undefined {
  for (const item of ALL_NAV_ITEMS) {
    if (item.subItems) {
      const sub = item.subItems.find(s => pathname === s.path);
      if (sub) return sub;
    }
    if (pathname === item.path) return item;
  }
  return undefined;
}
